import styles from "./SettingsAuthentication.module.scss";
import useSettingsAuthentication from "./useSettingsAuthentication";
import Card from "../../../common/Card";
import React, {useState} from "react";
import {GoogleLoginButton} from "../../../common/GoogleLoginButton";
import Form from "react-bootstrap/Form";
import Button from "../../../common/Button";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import InputGroup from "react-bootstrap/InputGroup";
import useToast from "../../../util/useToast";
import {rxUpdateToken} from "../../../slices/appSlice";
import {useDispatch} from "react-redux";
import {jwtDecode} from "jwt-decode";
import clsx from "clsx";
import {Content, ContentFixed} from "../../../common/Content";
import {SettingsMenuMobile} from "../Settings";
import {SettingsContent} from "../SettingsContent";
import {SettingsTopMenu} from "../SettingsTopMenu";
import useFirebase from "../../../util/useFirebase";

const SettingsAuthentication = () => {
  const {
    t,
    linkGoogle,
    unlinkGoogleAccount,
    user,
    token
  } = useSettingsAuthentication();

  const {ProviderId} = useFirebase();

  const hasGoogleProvider = user.providerData.some(pd => pd.providerId === ProviderId.GOOGLE);
  const hasPasswordProvider = user.providerData.some(pd => pd.providerId === ProviderId.PASSWORD);

  return (
    <Content>
      <ContentFixed>
        <SettingsTopMenu title="header_title_settings_authentication"/>

        <SettingsMenuMobile/>
      </ContentFixed>

      <SettingsContent>
        <Card>
          <div className={styles.cardContent}>
            <h3 className={styles.title}>{t("title_google")}</h3>

            {hasGoogleProvider && !hasPasswordProvider && <>{t("text_only_google_account_infos")}</>}

            {hasGoogleProvider && hasPasswordProvider &&
              <a href="/settings" onClick={unlinkGoogleAccount}>{t("btn_unlink_google_account")}</a>}

            {!hasGoogleProvider && <>
              <p>{t("text_link_google_account_infos")}</p>

              <div className={styles.googleButton}>
                <GoogleLoginButton label={t("btn_link_google_account")} onClick={linkGoogle}/>
              </div>
            </>}

            {!hasPasswordProvider && <>
              <h3 className={clsx(styles.title, "mt-5")}>{t("title_email_address_password")}</h3>

              <RegisterEmailPasswordForm t={t} token={token}/>
            </>}

            {hasPasswordProvider && <>
              <h3 className={clsx(styles.title, "mt-5")}>{t("title_email_address")}</h3>

              <UpdateEmailForm t={t} token={token} hasGoogleProvider={hasGoogleProvider}/>

              <h3 className={clsx(styles.title, "mt-5")}>{t("title_password")}</h3>

              <UpdatePasswordForm t={t} token={token}/>
            </>}
          </div>
        </Card>
      </SettingsContent>
    </Content>
  );
};

const UpdateEmailForm = ({t, token, hasGoogleProvider}) => {
  const {showToastSuccess} = useToast();

  const {
    EmailAuthProvider,
    getAuth,
    ProviderId,
    reauthenticateWithCredential,
    unlink,
    updateEmail,
  } = useFirebase();

  const dispatch = useDispatch();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newEmail, setNewEmail] = useState("");

  const [updating, setUpdating] = useState(false);

  const [errors, setErrors] = useState({});

  const canUpdate = currentPassword.length > 0 && newEmail.length > 0 && !updating;

  const onSubmit = e => {
    e.preventDefault();

    const newErrors = {};

    if (!/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(newEmail)) {
      newErrors.newEmail = "error_new_email_invalid";
    } else if (newEmail === token.email) {
      newErrors.newEmail = "error_new_email_same";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length) {
      return;
    }

    setUpdating(true);

    const user = getAuth().currentUser;

    reauthenticateWithCredential(user, EmailAuthProvider.credential(token.email, currentPassword))
      .then(() => updateEmail(user, newEmail))
      .then(() => hasGoogleProvider ? unlink(user, ProviderId.GOOGLE) : Promise.resolve())
      .then(() => {
        setCurrentPassword("");
        setNewEmail("");

        showToastSuccess(t("toast_email_updated"));

        dispatch(rxUpdateToken({token: jwtDecode(getAuth().currentUser.accessToken)}));
      })
      .catch((error) => {
        if (error.code === "auth/wrong-password") {
          setErrors({currentPassword: "error_current_password"});
        } else if (error.code === "auth/email-already-in-use") {
          setErrors({newEmail: "error_email_already_in_user"});
        } else if (error.code === "auth/too-many-requests") {
          setErrors({currentPassword: "error_current_password_too_many_requests"});
        } else {
          console.error(error);
        }
      })
      .finally(() => setUpdating(false));
  };

  return <Form onSubmit={onSubmit} className={styles.form}>
    <Row>
      <Col sm={5} md={4} lg={4}/>
      <Col sm={6} className={styles.textInfo}>
        {t(hasGoogleProvider ? "text_update_email_infos_with_google_warning" : "text_update_email_infos")}
      </Col>
    </Row>

    <Form.Group as={Row} className="mb-2" controlId="update-email-password">
      <Form.Label column sm={5} md={4} lg={4}>{t("label_password_current")}</Form.Label>
      <Col sm={7} md={6} lg={4}>
        <InputGroup hasValidation>
          <Form.Control
            type="password"
            name="update-email-password"
            value={currentPassword}
            onChange={e => setCurrentPassword(e.target.value)}
            disabled={updating}
            autoComplete="on"
            isInvalid={!!errors.currentPassword}
          />
          <Form.Control.Feedback type="invalid">{t(errors.currentPassword)}</Form.Control.Feedback>
        </InputGroup>
      </Col>
    </Form.Group>
    <Form.Group as={Row} className="mb-2" controlId="update-email-new">
      <Form.Label column sm={5} md={4} lg={4}>{t("label_email_new")}</Form.Label>
      <Col sm={7} md={6} lg={4}>
        <InputGroup hasValidation>
          <Form.Control
            type="text"
            name="update-email-new"
            value={newEmail}
            onChange={e => setNewEmail(e.target.value)}
            disabled={updating}
            isInvalid={!!errors.newEmail}
          />
          <Form.Control.Feedback type="invalid">{t(errors.newEmail)}</Form.Control.Feedback>
        </InputGroup>
      </Col>
    </Form.Group>

    <Row>
      <Col sm={5} md={4} lg={4}/>
      <Col sm={7} md={6} lg={4}>
        <Button type="submit" variant="primary" disabled={!canUpdate}>{t("btn_submit_update_email")}</Button>
      </Col>
    </Row>
  </Form>;
};

const UpdatePasswordForm = ({t, token}) => {
  const {showToastSuccess} = useToast();

  const {
    EmailAuthProvider,
    getAuth,
    reauthenticateWithCredential,
    updatePassword
  } = useFirebase();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [updating, setUpdating] = useState(false);

  const [errors, setErrors] = useState({});

  const canUpdate = currentPassword.length > 0 && newPassword.length > 0 && confirmPassword.length > 0 && !updating;

  const onSubmit = e => {
    e.preventDefault();

    const newErrors = {};

    if (newPassword.length < 8 || !/\d+/.test(newPassword) || !/[a-zA-Z]+/.test(newPassword)) {
      newErrors.newPassword = "error_new_password_invalid";
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = "error_password_confirmation_must_match";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length) {
      return;
    }

    setUpdating(true);

    const user = getAuth().currentUser;

    reauthenticateWithCredential(user, EmailAuthProvider.credential(token.email, currentPassword))
      .then(() => updatePassword(user, newPassword))
      .then(() => {
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");

        showToastSuccess(t("toast_password_updated"));
      })
      .catch((error) => {
        if (error.code === "auth/wrong-password") {
          setErrors({currentPassword: "error_current_password"});
        } else if (error.code === "auth/too-many-requests") {
          setErrors({currentPassword: "error_current_password_too_many_requests"});
        } else {
          console.error(error);
        }
      })
      .finally(() => setUpdating(false));
  };

  return <Form onSubmit={onSubmit} className={styles.form}>
    <Row>
      <Col sm={5} md={4} lg={4}/>
      <Col sm={6} className={styles.textInfo}>
        {t("text_update_password_infos")}
      </Col>
    </Row>

    <Form.Group as={Row} className="mb-2" controlId="update-password-current">
      <Form.Label column sm={5} md={4} lg={4}>{t("label_password_current")}</Form.Label>
      <Col sm={7} md={6} lg={4}>
        <InputGroup hasValidation>
          <Form.Control
            type="password"
            name="update-password-current"
            value={currentPassword}
            onChange={e => setCurrentPassword(e.target.value)}
            disabled={updating}
            autoComplete="on"
            isInvalid={!!errors.currentPassword}
          />
          <Form.Control.Feedback type="invalid">{t(errors.currentPassword)}</Form.Control.Feedback>
        </InputGroup>
      </Col>
    </Form.Group>
    <Form.Group as={Row} className="mb-2" controlId="update-password-new">
      <Form.Label column sm={5} md={4} lg={4}>{t("label_password_new")}</Form.Label>
      <Col sm={7} md={6} lg={4}>
        <InputGroup hasValidation>
          <Form.Control
            type="password"
            name="update-password-new"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            disabled={updating}
            autoComplete="on"
            isInvalid={!!errors.newPassword}
          />
          <Form.Control.Feedback type="invalid">{t(errors.newPassword)}</Form.Control.Feedback>
        </InputGroup>
      </Col>
    </Form.Group>
    <Form.Group as={Row} className="mb-2" controlId="update-password-confirm">
      <Form.Label column sm={5} md={4} lg={4}>{t("label_password_confirmation")}</Form.Label>
      <Col sm={7} md={6} lg={4}>
        <InputGroup hasValidation>
          <Form.Control
            type="password"
            name="update-password-confirm"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            disabled={updating}
            autoComplete="on"
            isInvalid={!!errors.confirmPassword}
          />
          <Form.Control.Feedback type="invalid">{t(errors.confirmPassword)}</Form.Control.Feedback>
        </InputGroup>
      </Col>
    </Form.Group>

    <Row>
      <Col sm={5} md={4} lg={4}/>
      <Col sm={7} md={6} lg={4}>
        <Button type="submit" variant="primary" disabled={!canUpdate}>{t("btn_submit_update_password")}</Button>
      </Col>
    </Row>
  </Form>;
};

const RegisterEmailPasswordForm = ({t, token}) => {
  const {showToastSuccess} = useToast();

  const {
    EmailAuthProvider,
    getAuth,
    GoogleAuthProvider,
    linkWithCredential
  } = useFirebase();

  const dispatch = useDispatch();

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [updating, setUpdating] = useState(false);

  const [errors, setErrors] = useState({});

  const canUpdate = newPassword.length > 0 && confirmPassword.length > 0 && !updating;

  const onSubmit = e => {
    e.preventDefault();

    const newErrors = {};

    if (newPassword.length < 8 || !/\d+/.test(newPassword) || !/[a-zA-Z]+/.test(newPassword)) {
      newErrors.newPassword = "error_new_password_invalid";
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = "error_password_confirmation_must_match";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length) {
      return;
    }

    setUpdating(true);

    const user = getAuth().currentUser;

    const googleAuthProvider = new GoogleAuthProvider();
    googleAuthProvider.setCustomParameters({login_hint: token.email});

    // reauthenticateWithRedirect(user, googleAuthProvider)
    Promise.resolve()
      .then(() => linkWithCredential(user, EmailAuthProvider.credential(token.email, newPassword)))
      .then(() => {
        setNewPassword("");
        setConfirmPassword("");

        showToastSuccess(t("toast_email_password_activated"));

        dispatch(rxUpdateToken({token: jwtDecode(getAuth().currentUser.accessToken)}));
      })
      .catch((error) => {
        if (error.code === "auth/user-mismatch") {
          setErrors({email: "error_email_account_mistmatch"});
        } else {
          console.error(error);
        }
      })
      .finally(() => setUpdating(false));
  };

  return <Form onSubmit={onSubmit} className={styles.form}>
    <Row>
      <Col sm={5} md={4} lg={4}/>
      <Col sm={6} className={styles.textInfo}>
        {t("text_add_email_password_infos")}
      </Col>
    </Row>

    <Form.Group as={Row} className="mb-2" controlId="activate-email-current">
      <Form.Label column sm={5} md={4} lg={4}>{t("label_email_current")}</Form.Label>
      <Col sm={7} md={6} lg={4}>
        <Form.Control
          type="text"
          name="activate-email-current"
          value={token.email}
          disabled
          isInvalid={!!errors.email}
        />
        <Form.Control.Feedback type="invalid">{t(errors.email)}</Form.Control.Feedback>
      </Col>
    </Form.Group>
    <Form.Group as={Row} className="mb-2" controlId="activate-password-new">
      <Form.Label column sm={5} md={4} lg={4}>{t("label_password_new")}</Form.Label>
      <Col sm={7} md={6} lg={4}>
        <InputGroup hasValidation>
          <Form.Control
            type="password"
            name="activate-password-new"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            disabled={updating}
            autoComplete="on"
            isInvalid={!!errors.newPassword}
          />
          <Form.Control.Feedback type="invalid">{t(errors.newPassword)}</Form.Control.Feedback>
        </InputGroup>
      </Col>
    </Form.Group>
    <Form.Group as={Row} className="mb-2" controlId="activate-password-confirm">
      <Form.Label column sm={5} md={4} lg={4}>{t("label_password_confirmation")}</Form.Label>
      <Col sm={7} md={6} lg={4}>
        <InputGroup hasValidation>
          <Form.Control
            type="password"
            name="activate-password-confirm"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            disabled={updating}
            autoComplete="on"
            isInvalid={!!errors.confirmPassword}
          />
          <Form.Control.Feedback type="invalid">{t(errors.confirmPassword)}</Form.Control.Feedback>
        </InputGroup>
      </Col>
    </Form.Group>

    <Row>
      <Col sm={5} md={4} lg={4}/>
      <Col sm={7} md={6} lg={4}>
        <Button type="submit" variant="primary" disabled={!canUpdate}>{t("btn_submit_add_password")}</Button>
      </Col>
    </Row>
  </Form>;
};

export default SettingsAuthentication;
